import { TaskResponse } from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import { NavigateFunction } from "react-router-dom";
import Path from "src/constants/path";
import { addSentryBreadcrumb } from "src/lib/addSentryBreadcrumb";
import { KnownProgram } from "src/constants/programs";
import TaskManagementBloc, {
  TaskKey,
  TaskObserverEvent
} from "src/state/TaskManagementBloc/TaskManagementBloc";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import { userState } from "src/state/state";
import type { TranslationKey } from "src/types/translationKey";
import NavIconChatActive from "src/ui/assets/icons/nav-chat-active.svg";
import NavIconChat from "src/ui/assets/icons/nav-chat.svg";
import NavIconHomeActive from "src/ui/assets/icons/nav-home-active.svg";
import NavIconHome from "src/ui/assets/icons/nav-home.svg";
import NavIconLabsActive from "src/ui/assets/icons/nav-labs-active.svg";
import NavIconLabs from "src/ui/assets/icons/nav-labs.svg";
import NavIconMealsActive from "src/ui/assets/icons/nav-meals-active.svg";
import NavIconMeals from "src/ui/assets/icons/nav-meals.svg";
import NavIconProfileActive from "src/ui/assets/icons/nav-profile-active.svg";
import NavIconProfile from "src/ui/assets/icons/nav-profile.svg";
import NavIconLearnActive from "src/ui/assets/icons/nav-learn-active.svg";
import NavIconLearn from "src/ui/assets/icons/nav-learn.svg";
import { PARTNER_CONFIGS } from "src/constants/partnerConfig";
import { KnownPartnerSources } from "src/constants/partner";

export { KnownPartnerSources } from "src/constants/partner";

export interface AppViewData {
  label: TranslationKey;
  title?: string;
  path: Path;
  icon?: {
    active: string;
    normal: string;
  };
}

export const navItems: AppViewData[] = [
  {
    label: "nav_home",
    path: Path.home,
    icon: {
      active: NavIconHomeActive,
      normal: NavIconHome
    }
  },
  {
    label: "nav_messages",
    path: Path.chat,
    icon: {
      active: NavIconChatActive,
      normal: NavIconChat
    }
  },
  {
    label: "nav_learn",
    path: Path.journey,
    icon: {
      active: NavIconLearnActive,
      normal: NavIconLearn
    }
  },
  {
    label: "nav_meals",
    path: Path.meals,
    icon: {
      active: NavIconMealsActive,
      normal: NavIconMeals
    }
  },
  {
    label: "nav_lab_results",
    path: Path.labResults,
    icon: {
      active: NavIconLabsActive,
      normal: NavIconLabs
    }
  },
  {
    label: "nav_profile",
    path: Path.profile,
    icon: {
      active: NavIconProfileActive,
      normal: NavIconProfile
    }
  }
];

export enum OnboardingScreenStatus {
  completed = "completed",
  initial = "initial",
  showFull = "showFull",
  showVisitOnly = "showVisitOnly"
}

export interface AppViewState {
  view: AppViewData;
  showTabBar?: boolean;
  showBackButton?: boolean;
  showLogo?: boolean;
  showTitle?: string;
  hasAccess?: boolean;
  forceSkeletons?: boolean;
  escalationOccurred?: boolean;
  showForceMedicalQuestionnaire?: boolean;
  partnerSource?: KnownPartnerSources;
  partnerStyles?: boolean;
  mobileTabNavigation?: boolean;
  inApp: boolean;
  partnerSession?: boolean;
  showOnboardingScreen: OnboardingScreenStatus;
  allowIonContentScrollY: boolean;
}

export default class AppViewCubit extends Cubit<AppViewState> {
  static keepAlive = true;
  private backButtonInstances = 0;
  private showLogoInstances = 0;
  private titleList: string[] = [];
  navigate: NavigateFunction = () => {};
  overlayIds = new Set<string>();
  private taskObserver?: () => void;
  chatBloc?: unknown;
  private messagesState?: unknown;

  static readonly initialState: AppViewState = {
    inApp: false,
    view: {
      label: "nav_home",
      path: Path.app
    },
    showTabBar: true,
    forceSkeletons: false,
    showOnboardingScreen: OnboardingScreenStatus.initial,
    allowIonContentScrollY: true
  };

  constructor() {
    super({ ...AppViewCubit.initialState });
    this.setPartnerSession();
    this.setupTaskObserver();
  }

  checkForFullOnboardingActive = async (): Promise<boolean> => {
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    const completeOnboardingTask = taskManagementState.getTask(
      TaskKey.COMPLETE_ONBOARDING
    );
    const onboardingAvailable =
      completeOnboardingTask?.status === TaskResponse.status.AVAILABLE;

    const initialSyncVisitTask = taskManagementState.getTask(
      TaskKey.INITIAL_SYNC_VISIT
    );
    const initialSyncVisitTaskInFinalStatus =
      taskManagementState.isTaskStatusFinal(initialSyncVisitTask);

    const showOnboardingScreen =
      onboardingAvailable && !initialSyncVisitTaskInFinalStatus;

    return showOnboardingScreen;
  };

  checkForVisitOnlyOnboardingActive = async (): Promise<boolean> => {
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    const completeOnboardingTask = taskManagementState.getTask(
      TaskKey.PRE_COMPLETE_ONBOARDING
    );

    const onboardingAvailable =
      completeOnboardingTask?.status === TaskResponse.status.AVAILABLE;

    return onboardingAvailable;
  };

  checkForOnboardingCompleted = async (
    options: { loadTasks?: boolean } = {}
  ) => {
    const { loadTasks = true } = options;
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    if (loadTasks) {
      await taskManagementState.loadProgramTasks(KnownProgram.ONBOARDING);
    }
    const userPreferences = Blac.getBloc(UserPreferencesCubit);
    const [visitOnly, full] = await Promise.all([
      this.checkForVisitOnlyOnboardingActive(),
      this.checkForFullOnboardingActive(),
      userPreferences.loadUserPreferences()
    ]);

    const userHasVisitOnly = userPreferences.userHasVisitOnlyOnboarding;
    const { userHasUnblockingOnboarding } = userPreferences;

    let showScreen = OnboardingScreenStatus.completed;

    // check visit only first
    if (visitOnly && userHasVisitOnly) {
      showScreen = OnboardingScreenStatus.showVisitOnly;
    }

    // check full onboarding
    if (full && !userHasVisitOnly && !userHasUnblockingOnboarding) {
      showScreen = OnboardingScreenStatus.showFull;
    }

    // if no onboarding is available, set the state to completed
    return this.setShowOnboardingScreen(showScreen);
  };

  setupTaskObserver = () => {
    // Listen for task changes and re-check onboarding status
    const taskManagementState = Blac.getBloc(TaskManagementBloc);
    this.taskObserver = taskManagementState.addObserver(
      TaskObserverEvent.TASK_LIST_CHANGED,
      (programs) => {
        // Check if onboarding program tasks have changed
        const onboardingTasks = programs.get(KnownProgram.ONBOARDING);
        if (onboardingTasks) {
          // Re-check onboarding completion when onboarding tasks change
          void this.checkForOnboardingCompleted({ loadTasks: false });
          addSentryBreadcrumb(
            "appView",
            "Re-checked onboarding completion due to task changes"
          );
        }
      }
    );
  };

  cleanup = () => {
    // Remove task observer when cubit is destroyed
    if (this.taskObserver) {
      this.taskObserver();
      this.taskObserver = undefined;
    }
  };

  setPartnerSource(source: KnownPartnerSources | string) {
    const match = Object.values(KnownPartnerSources).find((s) =>
      source.toLowerCase().includes(s.toLowerCase())
    );

    if (match && match !== this.state.partnerSource) {
      this.emit({ ...this.state, partnerSource: match });
    }
  }

  setInApp = (inApp: boolean): void => {
    this.emit({ ...this.state, inApp });
  };

  /**
   * Resolves which partner (if any) the current session belongs to, by
   * matching each `PARTNER_CONFIGS` entry's `?from=` query param and auth
   * method against the current session (UA match wins on ties, then query
   * param, then auth method).
   *
   * The live webview UA check is resolved separately from — and drives
   * `mobileTabNavigation` independently of — the other two signals: a
   * partner's `isEmbeddedWebview` capability must only produce webview chrome
   * while the member is actually inside that webview right now, not merely
   * because they belong to a partner that has one (e.g. a Transcarent member
   * authenticated via TRANSCARENT_SSO outside the webview gets branding but
   * not webview chrome).
   *
   * `isPartnerSession` ORs with the current `partnerSession` state (see
   * `disablePartnerSession`), so once a partner session is detected it stays
   * "sticky" across re-renders that no longer carry the original signal.
   */
  setPartnerSession = (
    setIsPartnerSession?: boolean,
    forcePartner?: KnownPartnerSources
  ): void => {
    const urlQuery = new URLSearchParams(window.location.search);
    const fromQuery = urlQuery.get("from");

    let userSessionMethod: string | undefined;
    try {
      userSessionMethod = userState.state.userData?.sessionAuthenticationMethod;
    } catch {
      // userState may not be initialized yet during construction
    }

    // Live webview detection: independent of the other signals below, since a
    // partner's own webview chrome (tab-style nav) must only be on while the
    // member is actually inside that webview right now — not merely because
    // they belong to a partner that embeds one (e.g. a Transcarent member
    // authenticated outside the webview must not get webview chrome either).
    const uaMatch = Object.entries(PARTNER_CONFIGS).find(
      ([, config]) =>
        config.embeddedWebviewUserAgentSubstring &&
        navigator.userAgent.includes(config.embeddedWebviewUserAgentSubstring)
    );

    const queryMatch = Object.entries(PARTNER_CONFIGS).find(
      ([, config]) =>
        config.detectQueryParam && config.detectQueryParam === fromQuery
    );

    const authMatch = Object.entries(PARTNER_CONFIGS).find(
      ([, config]) => config.ssoAuthMethod === userSessionMethod
    );

    const matchedSource = (uaMatch ?? queryMatch ?? authMatch)?.[0] as
      | KnownPartnerSources
      | undefined;

    const isPartnerSession = Boolean(
      this.state.partnerSession ||
      setIsPartnerSession ||
      forcePartner ||
      matchedSource
    );

    const partnerSource =
      forcePartner ?? matchedSource ?? this.state.partnerSource;

    this.emit({
      ...this.state,
      partnerSession: isPartnerSession,
      partnerSource: isPartnerSession ? partnerSource : undefined,
      partnerStyles: isPartnerSession,
      mobileTabNavigation: Boolean(uaMatch)
    });
    this.setPartnerSessionStyles();
  };

  setPartnerSessionStyles = (): void => {
    const html = document.querySelector("html");
    html?.classList.toggle("partner-session", this.state.partnerStyles);
    html?.classList.toggle("tab-nav", this.state.mobileTabNavigation);

    Object.entries(PARTNER_CONFIGS).forEach(([source, config]) => {
      html?.classList.toggle(
        config.cssClassName,
        Boolean(this.state.partnerStyles) && this.state.partnerSource === source
      );
    });
  };

  togglePartnerSession = (): void => {
    const next = !this.state.partnerSession;
    this.emit({ ...this.state, partnerSession: next });
    userState.simulateTranscarentSession(next);
  };

  /**
   * DEBUG: fully exit partner-session mode. `setPartnerSession()` ORs with the
   * current `partnerSession` state, so it can never turn the flag off on its
   * own — closing the debug panel must clear it explicitly or the app stays
   * stuck in partner-session UI after the simulation is disabled.
   */
  disablePartnerSession = (): void => {
    this.emit({
      ...this.state,
      partnerSession: false,
      partnerSource: undefined,
      partnerStyles: false
    });
    this.setPartnerSessionStyles();
  };

  togglePartnerStyles = (): void => {
    this.emit({ ...this.state, partnerStyles: !this.state.partnerStyles });
    this.setPartnerSessionStyles();
  };

  toggleMobileTabNavigation = (): void => {
    this.emit({
      ...this.state,
      mobileTabNavigation: !this.state.mobileTabNavigation
    });
    this.setPartnerSessionStyles();
  };

  public readonly setForceSkeletons = (set: boolean): void => {
    this.emit({ ...this.state, forceSkeletons: set });
  };

  public readonly setMessagesState = (messages: unknown): void => {
    this.messagesState = messages;
  };

  public readonly setOnboardingDone = (): void => {
    const userPreferences = Blac.getBloc(UserPreferencesCubit);
    if (userPreferences.state[UserPreferenceKeys.onboardingDone]) {
      return;
    }

    void userPreferences.updateUserPreferences({
      [UserPreferenceKeys.onboardingDone]: "true"
    });
    addSentryBreadcrumb("appView", `Set onboarding done`);
  };

  public readonly setPageByPath = (path: Path): void => {
    const newPage = navItems.find((item) => path.startsWith(item.path));
    if (newPage) {
      this.emit({ ...this.state, view: newPage });
    }
  };

  public readonly setTabBarVisible = (setTo: boolean): void => {
    this.emit({ ...this.state, showTabBar: setTo });
  };

  public readonly toggleTitle = (setTo: boolean, title: string): void => {
    if (setTo) {
      this.titleList = [...this.titleList, title];
    } else {
      this.titleList = this.titleList = this.titleList.filter(
        (t) => t !== title
      );
    }

    const showTitle = this.titleList[this.titleList.length - 1];
    this.emit({ ...this.state, showTitle });
  };

  public readonly updateBackButton = (added: boolean): void => {
    this.backButtonInstances += added ? 1 : -1;
    const show = this.backButtonInstances !== 0;
    if (show !== this.state.showBackButton) {
      this.emit({ ...this.state, showBackButton: show });
    }
  };

  public readonly updateShowLogo = (added: boolean): void => {
    this.showLogoInstances += added ? 1 : -1;
    const show = this.showLogoInstances !== 0;
    if (show !== this.state.showLogo) {
      this.emit({ ...this.state, showLogo: show });
    }
  };

  private escalationOccurredCallback?: () => void;

  public readonly setOnEscalationOccurredCallback = (
    cb: (() => void) | undefined
  ): void => {
    this.escalationOccurredCallback = cb;
  };

  public readonly setEscalationOccurred = (value: boolean): void => {
    this.patch({ escalationOccurred: value });
    if (value && this.escalationOccurredCallback) {
      this.escalationOccurredCallback();
    }
  };

  public readonly setShowOnboardingScreen = (
    status: OnboardingScreenStatus
  ) => {
    this.patch({
      showOnboardingScreen: status
    });
  };

  public readonly setAllowIonContentScrollY = (allow: boolean) => {
    this.patch({
      allowIonContentScrollY: allow
    });
  };
}
